import { Row } from 'antd';
import { keyBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, UserType } from 'api/models';
import { Project } from 'api/project';
import { Page } from 'components/molecules/page';
import { AvatarInviteTrigger } from 'components/organisms/projects/global-invite/avatar-invite-trigger';
import {
  LeftArea,
  OverviewWrapper,
  RightArea,
} from 'components/organisms/projects/project-overview';
import {
  CardNames,
  data,
} from 'components/organisms/projects/project-overview/api';
import {
  CardNameToComponentMapping,
  CustomisableLeftSectionCards,
  HighlightSection,
} from 'components/organisms/projects/project-overview/left-section';
import { RightSectionCards } from 'components/organisms/projects/project-overview/right-section-cards';
import { useProjectTasksRefresh } from 'components/pages/use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from 'components/pages/use-project-tasks-socket-updates';
import { RootState } from 'data';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useProjectWebsocket } from 'hooks/websockets';
import { FieldsStore, TaskField } from 'stores/fields';

export const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Title = styled(Page.Header.Title)`
  &&& {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ProjectOverviewPage: FC<{
  project: Project;
}> = ({ project }) => {
  const translate = useTranslate();
  const { user } = useUser();

  const { projectId } = project;
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const taskFields = useMemo(
    () =>
      keyBy(FieldsStore.enabledTaskFields(fieldsStore), 'fieldName') as Record<
        string,
        TaskField
      >,
    [fieldsStore],
  );
  const cardConfig = CardNameToComponentMapping(projectId, taskFields);

  useProjectTasksRefresh({ projectId: ProjectId(projectId) });
  useProjectTasksSocketUpdates(ProjectId(projectId));
  useProjectWebsocket(ProjectId(projectId));

  const projectOverviewCards = useMemo(
    () =>
      data.map((datum) => {
        if (
          (datum.cardName === CardNames.PlannedVsActual ||
            datum.cardName === CardNames.KeyEventsAndIntervals) &&
          user.userType === UserType.External
        ) {
          return {
            ...datum,
            hidden: true,
          };
        }

        return datum;
      }),
    [user],
  );

  return (
    <Page>
      <Page.Header
        compact
        sticky
        title={
          <TitleWrapper>
            <Title>{translate('projects.overview')}</Title>
            <AvatarInviteTrigger projectId={project.projectId} showButton />
          </TitleWrapper>
        }
      />
      <Page.Content>
        <OverviewWrapper>
          <LeftArea>
            <Row gutter={[16, 16]}>
              <HighlightSection projectId={projectId} />
              <CustomisableLeftSectionCards
                data={projectOverviewCards}
                cardConfig={cardConfig}
              />
            </Row>
          </LeftArea>
          <RightArea>
            <RightSectionCards projectId={projectId} />
          </RightArea>
        </OverviewWrapper>
      </Page.Content>
    </Page>
  );
};
